import React from 'react';
import { JournalEntry } from '../../../types';

const SETUP_TYPES = [
  'VWAP Reclaim Long', 'VWAP Rejection Short', 'OR Breakout Long', 'OR Failed Breakout Short',
  'Liquidity Sweep Reversal', 'Gamma Acceleration', 'Extension Fade', 'Trend Pullback', 'Max Pain Pin', 'Custom',
];
const REGIMES = ['Mean Reversion', 'Breakout', 'Trend Continuation', 'No Trade'];
const TAG_PRESETS = ['sweep', 'vwap-reclaim', 'or-breakout', 'trap', 'gamma', 'stopped-out', 'early-entry', 'held-too-long', 'size-error', 'execution-error'];

interface Props {
  form: Omit<JournalEntry, 'id'>;
  editId: number | null;
  onSave: () => void;
  onCancel: () => void;
  onChange: (patch: Partial<Omit<JournalEntry, 'id'>>) => void;
}

export function JournalEditor({ form, editId, onSave, onCancel, onChange }: Props) {
  const toggleTag = (tag: string) =>
    onChange({ tags: form.tags.includes(tag) ? form.tags.filter(t => t !== tag) : [...form.tags, tag] });

  return (
    <div className="card-inner mb12" style={{ borderLeft: '2px solid var(--blue)' }}>
      <div className="sl mb8">{editId ? 'Edit Entry' : 'New Trade'}</div>
      <div className="g3" style={{ gap: 6, marginBottom: 6 }}>
        <div className="field" style={{ marginBottom: 0 }}>
          <label>Symbol</label>
          <input value={form.symbol} onChange={e => onChange({ symbol: e.target.value.toUpperCase() })} />
        </div>
        <div className="field" style={{ marginBottom: 0 }}>
          <label>Date</label>
          <input type="date" value={form.date} onChange={e => onChange({ date: e.target.value })} />
        </div>
        <div className="field" style={{ marginBottom: 0 }}>
          <label>Direction</label>
          <select value={form.dir} onChange={e => onChange({ dir: e.target.value })}>
            <option value="">—</option><option value="long">Long</option><option value="short">Short</option>
          </select>
        </div>
        <div className="field" style={{ marginBottom: 0 }}>
          <label>Setup</label>
          <select value={form.setup} onChange={e => onChange({ setup: e.target.value })}>
            <option value="">Select…</option>
            {SETUP_TYPES.map(s => <option key={s}>{s}</option>)}
          </select>
        </div>
        <div className="field" style={{ marginBottom: 0 }}>
          <label>Regime at Entry</label>
          <select value={form.regime} onChange={e => onChange({ regime: e.target.value })}>
            <option value="">Select…</option>
            {REGIMES.map(r => <option key={r}>{r}</option>)}
          </select>
        </div>
        <div className="field" style={{ marginBottom: 0 }}>
          <label>Entry</label>
          <input type="number" step="0.01" value={form.entry ?? ''} onChange={e => onChange({ entry: e.target.value ? parseFloat(e.target.value) : null })} />
        </div>
        <div className="field" style={{ marginBottom: 0 }}>
          <label>Stop</label>
          <input type="number" step="0.01" value={form.stop ?? ''} onChange={e => onChange({ stop: e.target.value ? parseFloat(e.target.value) : null })} />
        </div>
        <div className="field" style={{ marginBottom: 0 }}>
          <label>Target</label>
          <input type="number" step="0.01" value={form.target ?? ''} onChange={e => onChange({ target: e.target.value ? parseFloat(e.target.value) : null })} />
        </div>
        <div className="field" style={{ marginBottom: 0 }}>
          <label>Exit</label>
          <input type="number" step="0.01" value={form.exit ?? ''} onChange={e => onChange({ exit: e.target.value ? parseFloat(e.target.value) : null })} />
        </div>
        <div className="field" style={{ marginBottom: 0 }}>
          <label>Result $</label>
          <input type="number" step="0.01" value={form.result ?? ''} onChange={e => onChange({ result: e.target.value ? parseFloat(e.target.value) : null })} placeholder="net P&L" />
        </div>
      </div>
      <div className="field mt8">
        <label>Notes / Thesis</label>
        <textarea value={form.notes} onChange={e => onChange({ notes: e.target.value })} placeholder="What was the thesis? What happened? What would you do differently?" />
      </div>
      <div style={{ marginBottom: 8 }}>
        <div style={{ fontSize: 10, color: 'var(--t3)', textTransform: 'uppercase', letterSpacing: '.8px', marginBottom: 4 }}>Tags</div>
        <div className="flex gap4" style={{ flexWrap: 'wrap' }}>
          {TAG_PRESETS.map(tag => (
            <button key={tag} className={`btn btn-sm ${form.tags.includes(tag) ? 'btn-primary' : ''}`} style={{ fontSize: 11 }} onClick={() => toggleTag(tag)}>{tag}</button>
          ))}
        </div>
      </div>
      <div className="flex gap6">
        <button className="btn btn-primary btn-sm" onClick={onSave} disabled={!form.symbol || !form.setup}>Save</button>
        <button className="btn btn-sm" onClick={onCancel}>Cancel</button>
      </div>
    </div>
  );
}
